var App = App || {};

App.NoWrapChevrons = (function () {
    'use strict';

    const CONFIG = {
        WRAPPER_CLASS: "nowrap-chevron",
        BUTTON_INNER_SELECTOR: ".btn > .btn-inner",
        ICON_CLASS: "icon",
        HIDDEN_SELECTOR: ".visually-hidden, .sr-only, [aria-hidden='true'], [hidden]"
    };

    // Glues each button's trailing icon (typically a chevron) to the last word of its label, so the icon can never
    // wrap onto a line by itself. Markup like
    //     <span class="text">Open an Account</span> <span class="icon fa fa-chevron-circle-right"></span>
    // becomes
    //     <span class="text">Open an <span class="nowrap-chevron">Account <span class="icon ..."></span></span></span>
    // Safe to call repeatedly on the same context: once moved, the icon is no longer a trailing child of .btn-inner.
    const initialize = (context = document) => {
        if (!context?.querySelectorAll) return;

        context.querySelectorAll(CONFIG.BUTTON_INNER_SELECTOR).forEach(buttonInner => {
            const icon = getTrailingIcon(buttonInner);
            if (!icon) return;

            const label = icon.previousElementSibling;
            if (!label || isHidden(label)) return;

            const textNode = findLastVisibleTextNode(label);
            if (!textNode) return;

            const gap = readGap(label, icon);
            if (gap === null) return;

            if (!canWrapApart(buttonInner, label)) return;

            glue(label, textNode, icon, gap);
        });
    };

    // The last visible element child of .btn-inner, if it is an icon. Leading icons (icon before text) are left alone.
    const getTrailingIcon = (buttonInner) => {
        const visibleChildren = Array.from(buttonInner.children).filter(child => !isHidden(child));
        const last = visibleChildren[visibleChildren.length - 1];
        return last?.classList.contains(CONFIG.ICON_CLASS) ? last : null;
    };

    const isHidden = (element) => element.matches(CONFIG.HIDDEN_SELECTOR);

    // Walks backwards through the label to find the last text node a sighted user sees, skipping screen-reader-only
    // content and descending into inline markup such as <strong>.
    const findLastVisibleTextNode = (root) => {
        const children = Array.from(root.childNodes).reverse();
        for (const node of children) {
            if (node.nodeType === Node.TEXT_NODE) {
                if (node.data.trim()) return node;
                continue;
            }
            if (node.nodeType !== Node.ELEMENT_NODE || isHidden(node)) continue;

            const nested = findLastVisibleTextNode(node);
            if (nested) return nested;
        }
        return null;
    };

    // Returns " " when whitespace separated the label from the icon, "" when they were flush, or null when anything
    // else (visible text) sits between them, in which case the button is left untouched.
    const readGap = (label, icon) => {
        let gap = "";
        for (let node = label.nextSibling; node && node !== icon; node = node.nextSibling) {
            if (node.nodeType === Node.COMMENT_NODE) continue;
            if (node.nodeType !== Node.TEXT_NODE || node.data.trim()) return null;
            gap = " ";
        }
        return gap;
    };

    // Nothing to fix when the icon is already its own flex/grid item, or when the label cannot wrap in the first place.
    const canWrapApart = (buttonInner, label) => {
        const innerDisplay = getComputedStyle(buttonInner).display;
        if (innerDisplay.includes("flex") || innerDisplay.includes("grid")) return false;

        const labelWhiteSpace = getComputedStyle(label).whiteSpace;
        return !(labelWhiteSpace.includes("nowrap") || labelWhiteSpace === "pre");
    };

    // Moves the icon into a nowrap wrapper alongside the label's last word, preserving the visual spacing.
    const glue = (label, textNode, icon, gap) => {
        const text = textNode.data;
        const trimmed = text.trimEnd();
        const lastWordStart = trimmed.search(/\S+$/);
        const before = trimmed.slice(0, lastWordStart);
        const lastWord = trimmed.slice(lastWordStart);
        const space = gap || (/\s$/.test(text) ? " " : "");

        // The whitespace that separated label and icon now lives inside the wrapper; drop the leftover nodes.
        for (let node = icon.previousSibling; node && node !== label;) {
            const previous = node.previousSibling;
            if (node.nodeType === Node.TEXT_NODE && !node.data.trim()) node.remove();
            node = previous;
        }

        const wrapper = document.createElement("span");
        wrapper.className = CONFIG.WRAPPER_CLASS;
        wrapper.append(lastWord + space, icon);

        textNode.after(wrapper);
        if (before) textNode.data = before;
        else textNode.remove();
    };

    return {
        initialize
    };

})();
